// AccountPDFDocument.tsx
import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
} from '@react-pdf/renderer';
import { AccountInfo, AccountPortfolio, AccountTrade, AccountTransfer, calculateTradingAnalysis, MonthlyPnLData } from '../../../types';
import { getReadableDate, getFormattedDate, getFormattedTime, getAccountTypeFromAddress, universalAddressToSymbol } from '../../../types/util';

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#FFFFFF',
        padding: 20,
        fontSize: 8,
        fontFamily: 'Helvetica',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 15,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#E0E0E0',
    },
    headerLeft: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#333',
    },
    headerRight: {
        fontSize: 8,
        color: '#666',
    },
    sectionContainer: {
        marginBottom: 12,
    },
    sectionTitle: {
        fontSize: 8,
        fontWeight: 'bold',
        marginBottom: 4,
        color: '#333',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    tableContainer: {
        borderWidth: 1,
        borderColor: '#E0E0E0',
        borderRadius: 2,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#F5F5F5',
        borderBottomWidth: 1,
        borderBottomColor: '#E0E0E0',
        paddingVertical: 4,
        paddingHorizontal: 6,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#F0F0F0',
        paddingVertical: 3,
        paddingHorizontal: 6,
    },
    tableRowLast: {
        flexDirection: 'row',
        paddingVertical: 3,
        paddingHorizontal: 6,
    },
    cellHeader: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#555',
        textTransform: 'uppercase',
    },
    cell: {
        fontSize: 8,
        color: '#333',
        paddingRight: 4,
    },
    cellRight: {
        fontSize: 8,
        color: '#333',
        textAlign: 'right',
        paddingRight: 0,
    },
    cellCenter: {
        fontSize: 8,
        color: '#333',
        textAlign: 'center',
        paddingRight: 4,
    },
    cellPositive: {
        fontSize: 8,
        color: '#2E7D32',
        textAlign: 'right',
        fontWeight: 'bold',
        paddingRight: 4,
    },
    cellNegative: {
        fontSize: 8,
        color: '#D32F2F',
        textAlign: 'right',
        fontWeight: 'bold',
        paddingRight: 4,
    },
    cellNeutral: {
        fontSize: 8,
        textAlign: 'right',
        paddingRight: 4,
    },
    summaryContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 12,
    },
    summaryBox: {
        width: '49%',
        backgroundColor: '#FAFAFA',
        padding: 8,
        borderRadius: 2,
        borderWidth: 1,
        borderColor: '#E0E0E0',
    },
    summaryTitle: {
        fontSize: 7,
        color: '#666',
        textTransform: 'uppercase',
        marginBottom: 2,
    },
    summaryValue: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#333',
    },
    noData: {
        fontSize: 8,
        color: '#999',
        textAlign: 'center',
        padding: 8,
        fontStyle: 'italic',
    },
    navContainer: {
        backgroundColor: '#F8F9FA',
        borderWidth: 1,
        borderColor: '#E0E0E0',
        borderRadius: 4,
        padding: 12,
        marginBottom: 12,
    },
    navTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#333',
        marginBottom: 8,
        textAlign: 'center',
    },
    navFlow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    navItem: {
        alignItems: 'center',
        flex: 1,
    },
    navItemTitle: {
        fontSize: 7,
        color: '#666',
        textTransform: 'uppercase',
        marginBottom: 2,
        textAlign: 'center',
    },
    navItemValue: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#333',
        textAlign: 'center',
    },
    navItemValuePositive: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#2E7D32',
        textAlign: 'center',
    },
    navItemValueNegative: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#D32F2F',
        textAlign: 'center',
    },
    navArrow: {
        fontSize: 12,
        color: '#666',
        marginHorizontal: 4,
    },
    navSummaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 8,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: '#E0E0E0',
    },
    navSummaryItem: {
        alignItems: 'center',
    },
    navSummaryTitle: {
        fontSize: 7,
        color: '#666',
        textTransform: 'uppercase',
        marginBottom: 2,
    },
    navSummaryValue: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#333',
    },
    navSummaryValuePositive: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#2E7D32',
    },
    navSummaryValueNegative: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#D32F2F',
    },
});

interface Props {
    portfolio: AccountPortfolio | null;
    firstPortfolio: AccountPortfolio | null;
    trades: AccountTrade[];
    transfers: AccountTransfer[];
    currency: 'eur' | 'usd' | 'chf';
    accountId: string;
    accountInfo: AccountInfo;
    dateRange: { from: Date; to: Date };
}

export const AccountPDFDocument = ({
    portfolio,
    firstPortfolio,
    trades,
    transfers,
    currency,
    accountId,
    accountInfo,
    dateRange
}: Props) => {
    const formatCurrency = (amount: number) => `${amount.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`;
    const formatAmount = (amount: number, decimals: number = 8) => amount.toFixed(decimals);

    // Calculate summary metrics
    const totalValue = portfolio?.valuation?.global?.[currency] || 0;
    const assetCount = portfolio ? Object.keys(portfolio.asset_data?.balances || {}).length : 0;
    const tradeCount = trades.length;
    const transferCount = transfers.length;

    // Calculate NAV change metrics
    const startValue = firstPortfolio?.valuation?.global?.[currency] || 0;
    const endValue = portfolio?.valuation?.global?.[currency] || 0;

    // Calculate net transfers (deposits - withdrawals)
    const transferMetrics = transfers.reduce((acc, transfer) => {
        const value = transfer.valuation?.global?.[currency] || 0;
        const cost = transfer.cost_valuation?.global?.[currency] || 0;
        const isDeposit = transfer.target === accountId;

        if (isDeposit) {
            acc.deposits += value;
        } else {
            acc.withdrawals += value;
        }
        acc.transferCosts += cost;

        return acc;
    }, { deposits: 0, withdrawals: 0, transferCosts: 0 });

    const netTransfers = transferMetrics.deposits - transferMetrics.withdrawals;

    // Calculate trading costs
    const tradingCosts = trades.reduce((sum, trade) => {
        return sum + (trade.cost_valuation?.global?.[currency] || 0);
    }, 0);

    const totalCosts = transferMetrics.transferCosts + tradingCosts;

    // Usage in your component:
    const tradingAnalysis = calculateTradingAnalysis(trades, transfers, firstPortfolio, portfolio, currency, { from: dateRange.from, to: dateRange.to });

    // Now you can easily access all the data:
    const {
        totalTrades,
        buyCount,
        sellCount,
        totalVolumeBought,
        totalVolumeSold,
        totalRealizedPnL,
        totalUnrealizedPnL,
        netPnL,
        monthlyData,
        assetPositions,
        winRate,
        avgTradeSize
    } = tradingAnalysis;

    // Check if period is at least one month
    const periodStart = dateRange.from;
    const periodEnd = dateRange.to;
    const daysDifference = Math.floor((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24));
    const showMonthlyChart = daysDifference >= 30;


    // Calculate expected vs actual end value for verification
    const expectedEndValue = startValue + netTransfers + netPnL - totalCosts;
    const navChange = endValue - startValue;
    const navChangePercent = startValue > 0 ? (navChange / startValue) * 100 : 0;

    const fxDeviation = -1 * ((startValue + totalRealizedPnL + totalUnrealizedPnL + totalCosts + netTransfers) - endValue)

    return (
        <Document>
            <Page size="A4" style={styles.page} orientation="landscape">
                {/* Header with Logo and Statement Info */}
                <View style={styles.header}>
                    {/* Logo */}
                    <View style={{ width: '30%' }}>
                        <Image
                            src="/logos/logo_black.png"
                            style={{
                                width: 170,
                                objectFit: 'contain'
                            }}
                        />
                    </View>

                    {/* Statement Information */}
                    <View style={{ width: '70%', alignItems: 'flex-end' }}>
                        <Text style={[styles.headerLeft, { fontSize: 14, marginBottom: 2 }]}>Account Statement</Text>
                        <Text style={[styles.headerRight, { fontSize: 9, marginBottom: 1 }]}>
                            {getFormattedDate(dateRange.from)} - {getFormattedDate(dateRange.to)}
                        </Text>
                    </View>
                </View>

                {/* Account Information Section */}
                <View style={styles.sectionContainer}>
                    <Text style={styles.sectionTitle}>Account Information</Text>
                    <View style={styles.tableContainer}>
                        <View style={styles.tableRow}>
                            <Text style={[styles.cell, { width: '25%', fontWeight: 'bold' }]}>Owner</Text>
                            <Text style={[styles.cell, { width: '75%' }]}>{accountInfo.name}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={[styles.cell, { width: '25%', fontWeight: 'bold' }]}>Account</Text>
                            <Text style={[styles.cell, { width: '75%' }]}>{accountId}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={[styles.cell, { width: '25%', fontWeight: 'bold' }]}>Type</Text>
                            <Text style={[styles.cell, { width: '75%' }]}>{getAccountTypeFromAddress(accountId)}</Text>
                        </View>
                        <View style={styles.tableRowLast}>
                            <Text style={[styles.cell, { width: '25%', fontWeight: 'bold' }]}>Currency</Text>
                            <Text style={[styles.cell, { width: '75%' }]}>{currency.toUpperCase()}</Text>
                        </View>
                    </View>
                </View>

                {/* NAV Change Analysis */}
                <View style={styles.sectionContainer}>
                    <Text style={styles.sectionTitle}>Net Asset Value</Text>
                    <View style={styles.tableContainer}>
                        {/* Starting NAV */}
                        <View style={styles.tableRow}>
                            <Text style={[styles.cell, { width: '60%', fontWeight: 'bold' }]}>Starting Value</Text>
                            <Text style={[styles.cellRight, { width: '40%', fontWeight: 'bold' }]}>{formatCurrency(startValue)}</Text>
                        </View>

                        {/* Net Transfers */}
                        <View style={styles.tableRow}>
                            <Text style={[styles.cell, { width: '60%' }]}>Deposits & Withdrawals</Text>
                            <Text style={[styles.cellRight, { width: '40%', color: netTransfers >= 0 ? '#2E7D32' : '#D32F2F' }]}>
                                {netTransfers >= 0 ? '+' : ''}{formatCurrency(netTransfers)}
                            </Text>
                        </View>

                        {/* Net P&L */}
                        <View style={styles.tableRow}>
                            <Text style={[styles.cell, { width: '60%' }]}>Net P&L</Text>
                            <Text style={[styles.cellRight, { width: '40%', color: netPnL >= 0 ? '#2E7D32' : '#D32F2F' }]}>
                                {netPnL >= 0 ? '+' : ''}{formatCurrency(netPnL)}
                            </Text>
                        </View>

                        {/* Total Costs */}
                        <View style={styles.tableRow}>
                            <Text style={[styles.cell, { width: '60%' }]}>Costs</Text>
                            <Text style={[styles.cellRight, { width: '40%', color: '#D32F2F' }]}>-{formatCurrency(totalCosts)}</Text>
                        </View>

                        {/* FX Deviation */}
                        <View style={styles.tableRow}>
                            <Text style={[styles.cell, { width: '60%' }]}>FX</Text>
                            <Text style={[styles.cellRight, { width: '40%', color: fxDeviation >= 0 ? '#2E7D32' : '#D32F2F' }]}>
                                {fxDeviation >= 0 ? '+' : ''}{formatCurrency(fxDeviation)}
                            </Text>
                        </View>

                        {/* Subtotal line */}
                        <View style={[styles.tableRow, { borderTopWidth: 1, borderTopColor: '#000', paddingTop: 4 }]}>
                            <Text style={[styles.cell, { width: '60%', fontWeight: 'bold' }]}>Ending Value</Text>
                            <Text style={[styles.cellRight, { width: '40%', fontWeight: 'bold' }]}>{formatCurrency(endValue)}</Text>
                        </View>

                        {/* Total Change */}
                        <View style={[styles.tableRowLast, { backgroundColor: '#F5F5F5', paddingVertical: 6 }]}>
                            <Text style={[styles.cell, { width: '60%', fontWeight: 'bold' }]}>
                                Total Change ({navChangePercent >= 0 ? '+' : ''}{navChangePercent.toFixed(2)}%)
                            </Text>
                            <Text style={[styles.cellRight, {
                                width: '40%',
                                fontWeight: 'bold',
                                color: navChange >= 0 ? '#2E7D32' : '#D32F2F'
                            }]}>
                                {navChange >= 0 ? '+' : ''}{formatCurrency(navChange)}
                            </Text>
                        </View>
                    </View>
                </View>

                {/* Current Positions */}
                {portfolio && (
                    <View style={styles.sectionContainer}>
                        <Text style={styles.sectionTitle}>Assets</Text>
                        <View style={styles.tableContainer}>
                            <View style={styles.tableHeader}>
                                <Text style={[styles.cellHeader, { width: '20%' }]}>Asset</Text>
                                <Text style={[styles.cellHeader, { width: '15%', textAlign: 'right' }]}>Amount</Text>
                                <Text style={[styles.cellHeader, { width: '15%', textAlign: 'right' }]}>Value</Text>
                                <Text style={[styles.cellHeader, { width: '15%', textAlign: 'right', paddingRight: 8 }]}>Share</Text>
                                <Text style={[styles.cellHeader, { width: '35%' }]}>Address</Text>
                            </View>
                            {Object.entries(portfolio.asset_data?.balances || {})
                                .sort(([, a], [, b]) => {
                                    const aVal = a.valuation?.global?.[currency] || 0;
                                    const bVal = b.valuation?.global?.[currency] || 0;
                                    return bVal - aVal;
                                })
                                .map(([assetId, balance], index, array) => {
                                    const value = balance.valuation?.global?.[currency] || 0;
                                    const share = portfolio.asset_data?.distribution?.[assetId] || 0;
                                    const isLast = index === array.length - 1;

                                    return (
                                        <View key={assetId} style={isLast ? styles.tableRowLast : styles.tableRow}>
                                            <Text style={[styles.cell, { width: '20%' }]}>
                                                {universalAddressToSymbol(balance.address) || assetId}
                                            </Text>
                                            <Text style={[styles.cellRight, { width: '15%' }]}>
                                                {formatAmount(balance.amount, 8)}
                                            </Text>
                                            <Text style={[styles.cellRight, { width: '15%' }]}>
                                                {formatCurrency(value)}
                                            </Text>
                                            <Text style={[styles.cellRight, { width: '15%', paddingRight: 8 }]}>
                                                {(share * 100).toFixed(2)}%
                                            </Text>
                                            <Text style={[styles.cell, { width: '35%', fontSize: 7 }]}>
                                                {balance.address}
                                            </Text>
                                        </View>
                                    );
                                })}
                            {Object.keys(portfolio.asset_data?.balances || {}).length === 0 && (
                                <Text style={styles.noData}>No positions available</Text>
                            )}
                        </View>
                    </View>
                )}

                {/* Transfer History */}
                <View style={styles.sectionContainer}>
                    <Text style={styles.sectionTitle}>Transfers</Text>
                    <View style={styles.tableContainer}>
                        <View style={styles.tableHeader}>
                            <Text style={[styles.cellHeader, { width: '10%' }]}>Date/Time</Text>
                            <Text style={[styles.cellHeader, { width: '10%' }]}>Type</Text>
                            <Text style={[styles.cellHeader, { width: '15%', textAlign: 'right' }]}>Amount</Text>
                            <Text style={[styles.cellHeader, { width: '15%', textAlign: 'right' }]}>Value </Text>
                            <Text style={[styles.cellHeader, { width: '15%', textAlign: 'right', paddingRight: 8 }]}>Cost </Text>
                            <Text style={[styles.cellHeader, { width: '35%' }]}>Details</Text>
                        </View>
                        {transfers.slice(0, 10).map((transfer, index) => {
                            const value = transfer.valuation?.global?.[currency] || 0;
                            const costValue = transfer.cost_valuation?.global?.[currency] || 0;
                            const isDeposit = transfer.target === accountId;
                            const isWithdrawal = transfer.source === accountId;
                            const type = transfer.transfer_type === 'reward' ? 'Reward' :
                                isDeposit ? 'Deposit' :
                                    isWithdrawal ? 'Withdraw' : 'Transfer';
                            const isLast = index === transfers.length - 1 || index === 9;

                            return (
                                <View key={transfer.transaction_id} style={isLast ? styles.tableRowLast : styles.tableRow}>
                                    <Text style={[styles.cell, { width: '10%', fontSize: 7 }]}>
                                        {getFormattedDate(transfer.timestamp)} {getFormattedTime(transfer.timestamp)}
                                    </Text>
                                    <Text style={[styles.cell, { width: '10%' }]}>
                                        {type}
                                    </Text>
                                    <Text style={[styles.cellRight, { width: '15%' }]}>
                                        {formatAmount(transfer.amount, 8)} {universalAddressToSymbol(transfer.asset)}
                                    </Text>
                                    <Text style={[styles.cellRight, { width: '15%' }]}>
                                        {formatCurrency(value)}
                                    </Text>
                                    <Text style={[styles.cellRight, { width: '15%', paddingRight: 8 }]}>
                                        {formatCurrency(costValue)}
                                    </Text>
                                    <Text style={[styles.cell, { width: '35%', fontSize: 7 }]}>
                                        {type === 'Deposit' ? `From: ${transfer.source}` :
                                            type === 'Withdraw' ? `To: ${transfer.target}` :
                                                `${transfer.source} → ${transfer.target}`}
                                    </Text>
                                </View>
                            );
                        })}
                        {transfers.length === 0 && (
                            <Text style={styles.noData}>No transfers in selected period</Text>
                        )}
                    </View>
                </View>
                <Text
                    style={{
                        position: 'absolute',
                        fontSize: 7,
                        bottom: 10,
                        left: 20,
                        color: '#666'
                    }}
                    fixed
                >
                    Generated on: {new Date().toLocaleString('de-DE', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </Text>
                <Text
                    style={{
                        position: 'absolute',
                        fontSize: 7,
                        bottom: 10,
                        right: 20,
                        color: '#666'
                    }}
                    render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
                    fixed
                />
            </Page>

            <Page size="A4" style={styles.page} orientation="landscape">
                {/* Header with Logo and Statement Info */}
                <View style={styles.header}>
                    {/* Logo */}
                    <View style={{ width: '30%' }}>
                        <Image
                            src="/logos/logo_black.png"
                            style={{
                                width: 170,
                                objectFit: 'contain'
                            }}
                        />
                    </View>

                    {/* Statement Information */}
                    <View style={{ width: '70%', alignItems: 'flex-end' }}>
                        <Text style={[styles.headerLeft, { fontSize: 14, marginBottom: 2 }]}>Trading Activity</Text>
                        <Text style={[styles.headerRight, { fontSize: 9, marginBottom: 1 }]}>
                            {getFormattedDate(dateRange.from)} - {getFormattedDate(dateRange.to)}
                        </Text>
                    </View>
                </View>
                {/* Trading Summary */}
                <View style={styles.sectionContainer}>
                    <Text style={styles.sectionTitle}>Trading Summary</Text>

                    {/* Key Metrics Summary */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                        <View style={[styles.summaryBox, { width: '18%' }]}>
                            <Text style={styles.summaryTitle}>Total Trades</Text>
                            <Text style={styles.summaryValue}>{totalTrades}</Text>
                            <Text style={[styles.summaryTitle, { fontSize: 6 }]}>
                                {buyCount} Buy | {sellCount} Sell
                            </Text>
                        </View>
                        <View style={[styles.summaryBox, { width: '18%' }]}>
                            <Text style={styles.summaryTitle}>Total Bought</Text>
                            <Text style={styles.summaryValue}>{formatCurrency(totalVolumeBought)}</Text>
                        </View>
                        <View style={[styles.summaryBox, { width: '18%' }]}>
                            <Text style={styles.summaryTitle}>Total Sold</Text>
                            <Text style={styles.summaryValue}>{formatCurrency(totalVolumeSold)}</Text>
                        </View>
                        <View style={[styles.summaryBox, { width: '18%' }]}>
                            <Text style={styles.summaryTitle}>Costs</Text>
                            <Text style={styles.summaryValue}>{formatCurrency(tradingCosts)}</Text>
                        </View>
                        <View style={[styles.summaryBox, { width: '25%' }]}>
                            <Text style={styles.summaryTitle}>P&L Breakdown</Text>
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                <View>
                                    <Text style={[styles.summaryValue, { fontSize: 8, color: totalRealizedPnL >= 0 ? '#2E7D32' : '#D32F2F' }]}>
                                        {formatCurrency(totalRealizedPnL)}
                                    </Text>
                                    <Text style={[styles.summaryTitle, { fontSize: 6 }]}>Realized</Text>
                                </View>
                                <View>
                                    <Text style={[styles.summaryValue, { fontSize: 8, color: totalUnrealizedPnL >= 0 ? '#2E7D32' : '#D32F2F' }]}>
                                        {formatCurrency(totalUnrealizedPnL)}
                                    </Text>
                                    <Text style={[styles.summaryTitle, { fontSize: 6 }]}>Unrealized</Text>
                                </View>
                            </View>
                            <Text style={[styles.summaryValue, {
                                fontSize: 8,
                                color: netPnL >= 0 ? '#2E7D32' : '#D32F2F',
                                marginTop: 2,
                                textAlign: 'center'
                            }]}>
                                <Text style={styles.summaryTitle}>Net: </Text>
                                {formatCurrency(netPnL)}
                            </Text>
                        </View>
                    </View>

                    {/* Asset Breakdown */}
                    <View style={styles.tableContainer}>
                        <View style={styles.tableHeader}>
                            <Text style={[styles.cellHeader, { width: '5%' }]}>Asset</Text>
                            <Text style={[styles.cellHeader, { width: '5%', textAlign: 'right' }]}>Trades</Text>
                            <Text style={[styles.cellHeader, { width: '20%', textAlign: 'right' }]}>Bought</Text>
                            <Text style={[styles.cellHeader, { width: '10%', textAlign: 'right' }]}>Buy Price</Text>
                            <Text style={[styles.cellHeader, { width: '20%', textAlign: 'right' }]}>Sold</Text>
                            <Text style={[styles.cellHeader, { width: '10%', textAlign: 'right' }]}>Sell Price</Text>
                            <Text style={[styles.cellHeader, { width: '10%', textAlign: 'right' }]}>Costs</Text>
                            <Text style={[styles.cellHeader, { width: '10%', textAlign: 'right' }]}>Realized P&L</Text>
                            <Text style={[styles.cellHeader, { width: '10%', textAlign: 'right' }]}>Unrealized P&L</Text>
                        </View>
                        {Object.entries(assetPositions)
                            .sort(([, a], [, b]) => (b.totalBuyValue + b.totalSellValue) - (a.totalBuyValue + a.totalSellValue))
                            .map(([assetId, asset], index, array) => {
                                const avgSellPrice = asset.sellTrades > 0 ? asset.totalSold / asset.sellTrades : 0;
                                const isLast = index === array.length - 1;

                                return (
                                    <View key={assetId} style={isLast ? styles.tableRowLast : styles.tableRow}>
                                        <Text style={[styles.cell, { width: '5%' }]}>{asset.symbol.toUpperCase()}</Text>
                                        <Text style={[styles.cellRight, { width: '5%' }]}>{asset.trades}</Text>
                                        <Text style={[styles.cellRight, { width: '20%' }]}>
                                            {formatAmount(asset.totalBuyAmount, 4)} {asset.symbol.toUpperCase()} | {formatCurrency(asset.totalBought)}
                                        </Text>
                                        <Text style={[styles.cellRight, { width: '10%' }]}>
                                            {formatCurrency(asset.totalBought / asset.totalBuyAmount || 0)}
                                        </Text>
                                        <Text style={[styles.cellRight, { width: '20%' }]}>
                                            {formatAmount(asset.totalSellAmount, 4)} {asset.symbol.toUpperCase()} | {formatCurrency(asset.totalSold)}
                                        </Text>
                                        <Text style={[styles.cellRight, { width: '10%' }]}>
                                            {formatCurrency(asset.totalSold / asset.totalSellAmount || 0)}
                                        </Text>
                                        <Text style={[styles.cellRight, { width: '10%' }]}>
                                            {formatCurrency(asset.totalCosts)}
                                        </Text>
                                        <Text
                                            style={[
                                                asset.realizedPnL === 0
                                                    ? styles.cellNeutral
                                                    : asset.realizedPnL > 0
                                                        ? styles.cellPositive
                                                        : styles.cellNegative,
                                                { width: '10%' },
                                            ]}
                                        >
                                            {formatCurrency(asset.realizedPnL)}
                                        </Text>

                                        <Text
                                            style={[
                                                asset.unrealizedPnL === 0
                                                    ? styles.cellNeutral
                                                    : asset.unrealizedPnL > 0
                                                        ? styles.cellPositive
                                                        : styles.cellNegative,
                                                { width: '10%' },
                                            ]}
                                        >
                                            {formatCurrency(asset.unrealizedPnL)}
                                        </Text>
                                    </View>
                                );
                            })}
                        {Object.keys(assetPositions).length === 0 && (
                            <Text style={styles.noData}>No trading activity in selected period</Text>
                        )}
                    </View>

                    {/* Monthly P&L Chart */}
                    {showMonthlyChart && Object.keys(monthlyData).length > 0 && (
                        <View style={{ marginTop: 8 }}>
                            <Text style={[styles.sectionTitle, { fontSize: 8, marginBottom: 4 }]}>Monthly P&L Progress</Text>
                            <View style={{
                                borderWidth: 1,
                                borderColor: '#E0E0E0',
                                padding: 4,
                                backgroundColor: '#FAFAFA'
                            }}>
                                {Object.entries(monthlyData)
                                    .sort(([a], [b]) => Number(a) - Number(b))
                                    .map(([yearStr, months]) => {
                                        const year = Number(yearStr);

                                        // Map months by 0-based index (0 = Jan, ..., 11 = Dec)
                                        const monthMap = new Map<number, MonthlyPnLData>();
                                        for (const m of months) {
                                            const monthIndex = new Date(m.month).getMonth();
                                            monthMap.set(monthIndex, m);
                                        }

                                        // Build full row with nulls where data is missing
                                        const fullRow: (MonthlyPnLData | null)[] = Array.from({ length: 12 }, (_, i) =>
                                            monthMap.get(i) ?? null
                                        );

                                        const maxPnL = Math.max(...fullRow.map(d => d ? Math.abs(d.realizedPnL) : 0));

                                        return (
                                            <View key={year} style={{ flexDirection: 'row', height: 80, alignItems: 'flex-end', marginBottom: 6 }}>
                                                {fullRow.map((data, monthIndex) => {
                                                    if (!data) {
                                                        return (
                                                            <View key={monthIndex} style={{
                                                                flex: 1,
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-end',
                                                                marginHorizontal: 1
                                                            }}>
                                                                <View style={{
                                                                    height: 1,
                                                                    width: '80%',
                                                                    backgroundColor: '#DDD',
                                                                    marginBottom: 4
                                                                }} />
                                                                <Text style={{
                                                                    fontSize: 7,
                                                                    fontWeight: 'bold',
                                                                    textAlign: 'center',
                                                                    color: '#CCC'
                                                                }}>
                                                                    {new Date(2000, monthIndex).toLocaleString('en-US', { month: 'short' })} '{String(year).slice(-2)}
                                                                </Text>
                                                            </View>
                                                        );
                                                    }

                                                    const barHeight = maxPnL > 0 ? Math.abs(data.realizedPnL) / maxPnL * 45 : 0;
                                                    const isPositive = data.realizedPnL >= 0;

                                                    return (
                                                        <View key={data.month} style={{
                                                            flex: 1,
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            marginHorizontal: 1
                                                        }}>
                                                            <Text style={{
                                                                fontSize: 6,
                                                                fontWeight: 'bold',
                                                                color: isPositive ? '#2E7D32' : '#D32F2F',
                                                                marginBottom: 2,
                                                                textAlign: 'center'
                                                            }}>
                                                                {Math.abs(data.realizedPnL) > 0 ? formatCurrency(data.realizedPnL) : ''}
                                                            </Text>

                                                            <View style={{
                                                                height: barHeight,
                                                                width: '80%',
                                                                backgroundColor: isPositive ? '#4CAF50' : '#F44336',
                                                                marginBottom: 4
                                                            }} />

                                                            <Text style={{
                                                                fontSize: 7,
                                                                fontWeight: 'bold',
                                                                textAlign: 'center',
                                                                color: '#333'
                                                            }}>
                                                                {data.monthName} '{data.year.toString().slice(-2)}
                                                            </Text>
                                                        </View>
                                                    );
                                                })}
                                            </View>
                                        );
                                    })}
                            </View>
                        </View>
                    )}

                </View>
                <Text
                    style={{
                        position: 'absolute',
                        fontSize: 7,
                        bottom: 10,
                        left: 20,
                        color: '#666'
                    }}
                    fixed
                >
                    Generated on: {new Date().toLocaleString('de-DE', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </Text>
                <Text
                    style={{
                        position: 'absolute',
                        fontSize: 7,
                        bottom: 10,
                        right: 20,
                        color: '#666'
                    }}
                    render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
                    fixed
                />
            </Page>
        </Document>
    );
};